import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, Filter as FilterIcon } from 'lucide-react';
import { tr } from '../../lib/i18nHelpers';
import { FilterValue, NumberFilterOp } from './types';
import { TableControls } from './useTableControls';

interface ThProps {
  controls: TableControls;
  colKey: string;
  align?: 'left' | 'right' | 'center';
  sortable?: boolean;
  filterable?: boolean;
  className?: string;
  children: React.ReactNode;
}

/** Sortable + filterable table header. Click the label to sort
 *  (asc → desc → off); the funnel opens a per-column condition filter. */
export function Th({ controls, colKey, align = 'left', sortable = true, filterable = true, className = '', children }: ThProps) {
  const col = controls.getColumn(colKey);
  const [open, setOpen] = useState(false);
  const thRef = useRef<HTMLTableCellElement>(null);
  const active = !!controls.getFilter(colKey);
  const sortState = controls.sort && controls.sort.key === colKey ? controls.sort.dir : 'none';

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (thRef.current && !thRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    const onScroll = () => setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  const alignCls = align === 'right' ? 'text-right justify-end' : align === 'center' ? 'text-center justify-center' : 'text-left';

  return (
    <th ref={thRef} data-testid={`th-${colKey}`} data-sort={sortState}
        className={`relative p-3 font-medium ${alignCls} ${className}`}>
      <div className={`flex items-center gap-1 ${align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : ''}`}>
        {sortable ? (
          <button type="button" data-testid={`th-sort-${colKey}`}
                  onClick={() => controls.toggleSort(colKey)}
                  className="flex items-center gap-0.5 font-medium hover:opacity-80">
            {children}
            <span className="inline-flex flex-col leading-none" aria-hidden>
              <ChevronUp className={`h-3 w-3 ${sortState === 'asc' ? 'text-primary' : 'text-muted-foreground/40'}`} />
              <ChevronDown className={`h-3 w-3 -mt-1 ${sortState === 'desc' ? 'text-primary' : 'text-muted-foreground/40'}`} />
            </span>
          </button>
        ) : children}
        {filterable && col && (
          <button type="button" data-testid={`th-filter-${colKey}`} data-active={active}
                  aria-label={tr('Filter', '篩選', '筛选')}
                  onClick={() => setOpen(o => !o)}
                  className={`p-0.5 rounded hover:bg-muted ${active ? 'text-primary' : 'text-muted-foreground/60'}`}>
            <FilterIcon className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {open && col && (
        <FilterPopover colKey={colKey} type={col.type} selectValues={col.selectValues}
                       controls={controls} onClose={() => setOpen(false)} />
      )}
    </th>
  );
}

function FilterPopover({ colKey, type, selectValues, controls, onClose }: {
  colKey: string;
  type: 'text' | 'number' | 'date' | 'select';
  selectValues?: string[];
  controls: TableControls;
  onClose: () => void;
}) {
  const current = controls.getFilter(colKey);
  const [q, setQ] = useState(current && current.type === 'text' ? current.q : '');
  const [op, setOp] = useState<NumberFilterOp>(current && current.type === 'number' ? current.op : 'eq');
  const [v1, setV1] = useState(current && current.type === 'number' && current.v1 !== undefined ? String(current.v1) : '');
  const [v2, setV2] = useState(current && current.type === 'number' && current.v2 !== undefined ? String(current.v2) : '');
  const [from, setFrom] = useState(current && current.type === 'date' ? current.from || '' : '');
  const [to, setTo] = useState(current && current.type === 'date' ? current.to || '' : '');
  const [sel, setSel] = useState(current && current.type === 'select' ? current.value : '');

  const apply = () => {
    if (type === 'text') controls.setFilter(colKey, { type: 'text', q });
    else if (type === 'number') {
      controls.setFilter(colKey, {
        type: 'number', op,
        v1: v1 === '' ? undefined : Number(v1),
        v2: v2 === '' ? undefined : Number(v2),
      });
    } else if (type === 'date') {
      controls.setFilter(colKey, { type: 'date', from: from || undefined, to: to || undefined });
    }
    onClose();
  };
  const clear = () => { controls.setFilter(colKey, undefined); onClose(); };

  const inputCls = 'w-full px-2 py-1.5 border rounded-md bg-background text-sm';

  return (
    <div data-testid={`filter-pop-${colKey}`}
         className="absolute right-0 top-full mt-1 z-30 w-60 bg-card border rounded-lg shadow-lg p-3 space-y-2 text-left">
      {type === 'text' && (
        <input data-testid={`filter-text-${colKey}`} value={q}
               onChange={e => setQ(e.target.value)}
               onKeyDown={e => { if (e.key === 'Enter') apply(); }}
               placeholder={tr('Contains…', '包含…', '包含…')}
               className={inputCls} />
      )}
      {type === 'number' && (
        <>
          <select data-testid={`filter-op-${colKey}`} value={op}
                  onChange={e => setOp(e.target.value as NumberFilterOp)} className={inputCls}>
            <option value="eq">=</option>
            <option value="ne">≠</option>
            <option value="gt">&gt;</option>
            <option value="lt">&lt;</option>
            <option value="ge">≥</option>
            <option value="le">≤</option>
            <option value="between">{tr('between', '之間', '之间')}</option>
          </select>
          <div className="flex gap-2">
            <input data-testid={`filter-v1-${colKey}`} type="number" value={v1}
                   onChange={e => setV1(e.target.value)} className={inputCls} />
            {op === 'between' && (
              <input data-testid={`filter-v2-${colKey}`} type="number" value={v2}
                     onChange={e => setV2(e.target.value)} className={inputCls} />
            )}
          </div>
        </>
      )}
      {type === 'date' && (
        <div className="flex gap-2">
          <input data-testid={`filter-from-${colKey}`} type="date" value={from}
                 onChange={e => setFrom(e.target.value)} className={inputCls} />
          <input data-testid={`filter-to-${colKey}`} type="date" value={to}
                 onChange={e => setTo(e.target.value)} className={inputCls} />
        </div>
      )}
      {type === 'select' && (
        <select data-testid={`filter-select-${colKey}`} value={sel}
                onChange={e => {
                  setSel(e.target.value);
                  controls.setFilter(colKey, e.target.value === ''
                    ? undefined
                    : { type: 'select', value: e.target.value });
                }}
                className={inputCls}>
          <option value="">{tr('(All)', '(全部)', '(全部)')}</option>
          {(selectValues || []).map(v => <option key={v} value={v}>{v}</option>)}
        </select>
      )}
      <div className="flex justify-end gap-2 pt-1">
        <button type="button" data-testid={`filter-clear-${colKey}`} onClick={clear}
                className="px-2.5 py-1 border rounded-md text-xs">
          {tr('Clear', '清除', '清除')}
        </button>
        {type !== 'select' && (
          <button type="button" data-testid={`filter-apply-${colKey}`} onClick={apply}
                  className="px-2.5 py-1 bg-primary text-primary-foreground rounded-md text-xs">
            {tr('Apply', '套用', '套用')}
          </button>
        )}
      </div>
    </div>
  );
}
